import cx from 'classnames';
import { useMemo } from 'react';
import { useDealershipServiceCoupons } from '~/context/dealership/StaticContext';
import formatCouponExpiration from '~/utilities/dealership/formatCouponExpiration';
import styles from './ServiceCoupons.module.css';

function getBarcodeWidths(code: string) {
  const widths: number[] = [];
  for (let i = 0; i < 12; i++) {
    const charCode = code.charCodeAt(i % code.length);
    widths.push((charCode % 3) + 1);
  }

  return widths;
}

function formatPrice(price: number): string {
  if (price === 0) return 'FREE';
  if (price < 0) return `${Math.abs(price)}% OFF`;

  return `$${price}`;
}

function useActiveServiceCoupons() {
  const serviceCoupons = useDealershipServiceCoupons();

  return useMemo(() => {
    const now = new Date();

    return serviceCoupons.filter(
      c => !c.expiration || new Date(c.expiration) > now
    );
  }, [serviceCoupons]);
}

export default function ServiceCoupons() {
  const serviceCoupons = useActiveServiceCoupons();

  return serviceCoupons.length > 0 ? (
    <section className="bg-canvas-muted px-lg py-4xl md:px-2xl md:py-[96px]">
      <div className="mx-auto max-w-[1272px]">
        <div className="mx-auto max-w-[720px] text-center">
          <div className="mb-lg text-text-primary t-label-xs tracking-widest">
            SERVICE MENU
          </div>
          <h2 className="mb-lg text-text-strong t-heading-3xl">
            Straightforward Pricing on the Services You Need
          </h2>
          <p className="text-text-default t-body-lg">
            No hidden fees. No surprise add-ons. Just honest pricing on quality
            service.
          </p>
        </div>

        <div className="mt-3xl grid grid-cols-1 gap-2xl sm:grid-cols-2 md:mt-4xl lg:grid-cols-3">
          {serviceCoupons.map(coupon => (
            <div
              key={coupon.title}
              className="relative flex min-h-[240px] flex-col overflow-hidden rounded-2xl border border-border-subtle bg-canvas-default p-2xl pl-2xl shadow-sm md:min-h-[290px] md:pl-[88px]"
            >
              <div className="absolute bottom-0 left-0 top-0 hidden w-[64px] flex-col items-center justify-between rounded-l-2xl bg-canvas-muted pb-2xl pt-xl md:flex">
                <div className={cx('text-text-subtle', styles.stubLabel)}>
                  Service Coupon
                </div>
                <div className="flex items-center gap-[2px] px-xs">
                  {getBarcodeWidths(coupon.code).map((w, idx) => (
                    <div
                      key={idx}
                      className="rounded-[1px] bg-text-strong opacity-[0.88]"
                      style={{ width: w, height: 24 }}
                    />
                  ))}
                </div>
                <div
                  className={cx('text-text-strong', styles.stubCode)}
                  data-testid="coupon-code"
                >
                  {coupon.code}
                </div>
              </div>

              <div
                className={cx(
                  'absolute left-[64px] top-[18px] bottom-[18px] hidden w-[1px] md:block',
                  styles.divider
                )}
              />

              <div className="hidden h-[22px] mb-[14px] md:block" />

              <div
                className={cx('mb-lg text-text-strong', styles.price)}
                data-testid="coupon-price"
              >
                {formatPrice(coupon.price)}
              </div>

              <div
                className="mb-xs text-text-strong t-strong-lg"
                data-testid="coupon-title"
              >
                {coupon.title}
              </div>

              <div
                className="flex-1 text-text-default t-body-xs"
                data-testid="coupon-description"
              >
                {coupon.description}
              </div>

              {coupon.expiration && (
                <div
                  className="mt-lg flex items-center justify-between border-t border-border-subtle pt-lg text-text-subtle t-label-xs"
                  data-testid="coupon-expiration"
                >
                  <span className="hidden sm:inline">Valid at Park Cities</span>
                  <span className="sm:hidden">Code: {coupon.code}</span>
                  <span className="tabular-nums text-text-default">
                    EXP {formatCouponExpiration(coupon.expiration)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-2xl text-center text-text-subtle t-body-xs tracking-widest">
          PRESENT COUPON AT TIME OF SERVICE. ONE PER VISIT. EXCLUDES TAXES AND
          SHOP SUPPLIES.
        </div>
      </div>
    </section>
  ) : null;
}
